import { Component, EventEmitter, Output } from '@angular/core';

/**
 * Reusable E-Invoicing Banner Component
 *
 * Architecture Design (SOLID Principles):
 * - Single Responsibility: This component has one clear purpose - display an informational
 *   banner with predefined content and handle click interactions. It does not manage routing
 *   or route detection (that's the parent's responsibility).
 *
 * - Dependency Inversion: The component depends on abstractions through @Output events.
 *   Parent components decide what happens on click, not this component.
 *
 * SSR Compatibility:
 * - This component is SSR-safe. It only renders markup and emits events.
 * - No browser-specific APIs (window, document, localStorage) are used.
 * - No lifecycle hooks require platform checks.
 *
 * Usage:
 * ```html
 * <app-e-invoicing-banner
 *   *ngIf="shouldShowBanner"
 *   (bannerClick)="handleBannerClick()">
 * </app-e-invoicing-banner>
 * ```
 */
@Component({
    selector: 'app-e-invoicing-banner',
    templateUrl: './e-invoicing-banner.component.html',
    styleUrls: ['./e-invoicing-banner.component.scss'],
})
export class EInvoicingBannerComponent {
    /**
     * Event emitted when the banner is clicked.
     * Parent components are responsible for handling navigation or other actions.
     * This follows Dependency Inversion - the component doesn't know about routing.
     */
    @Output() bannerClick = new EventEmitter<void>();

    /**
     * Handles click events on the banner.
     * Emits an event for parent components to handle.
     * SSR-safe: No browser-specific code.
     */
    protected onBannerClick(): void {
        this.bannerClick.emit();
    }
}
